// SSE streaming for section generation (ticket 8267).
// Follows the same fetch + ReadableStream pattern as chatApi.ts.

import { getEnv } from "@/config/env";

const API_BASE_URL = getEnv("VITE_API_BASE_URL") || "http://localhost:8010";

// Reuse the same token getter registered by AuthProvider for chat streams.
let tokenGetter: (() => Promise<string | null>) | null = null;
export function setGenerationTokenGetter(fn: () => Promise<string | null>) {
  tokenGetter = fn;
}

/** A content-intelligence citation mapped to section sources. */
export interface GenerationCitation {
  id: string;
  content: string;
  sourcefile: string;
  sourcepage: string;
  source_id?: string;
}

export interface GenerationStreamHandlers {
  onDelta?: (text: string) => void;
  onCitations?: (items: GenerationCitation[]) => void;
  onSection?: (section: unknown) => void;
  onStatus?: (data: { phase: string; message?: string }) => void;
  onError?: (detail: string, status?: number) => void;
  onDone?: () => void;
}

/** Stream section generation, dispatching each SSE event to `handlers`. */
export async function streamGenerate(
  sectionId: string,
  handlers: GenerationStreamHandlers,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const token = tokenGetter ? await tokenGetter() : null;
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${API_BASE_URL}/api/sections/${sectionId}/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      Accept: "text/event-stream",
    },
    body: "{}",
    signal: options.signal,
  });

  if (!response.ok || !response.body) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || `API error: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep = buffer.indexOf("\n\n");
    while (sep !== -1) {
      const rawEvent = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      sep = buffer.indexOf("\n\n");
      if (dispatchGenerationEvent(rawEvent, handlers)) return;
    }
  }
  handlers.onDone?.();
}

export function dispatchGenerationEvent(rawEvent: string, handlers: GenerationStreamHandlers): boolean {
  for (const line of rawEvent.split("\n")) {
    if (!line.startsWith("data:")) continue;
    const data = line.slice("data:".length).trim();
    if (!data) continue;
    if (data === "[DONE]") {
      handlers.onDone?.();
      return true;
    }
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(data);
    } catch {
      continue;
    }
    switch (parsed.type) {
      case "delta":
        handlers.onDelta?.(parsed.content as string);
        break;
      case "citations":
        handlers.onCitations?.(parsed.items as GenerationCitation[]);
        break;
      case "section":
        handlers.onSection?.(parsed.section);
        break;
      case "status":
        handlers.onStatus?.(parsed.data as { phase: string; message?: string });
        break;
      case "error":
        handlers.onError?.(parsed.detail as string, parsed.status as number | undefined);
        break;
    }
  }
  return false;
}
